import { Label } from "@/src/components/ui/label";
import { useState } from "react";
import { useFetch } from "@/src/lib/useFetch";
import { Combobox } from "@/src/components/ui/combobox";
import { fetch } from "@/src/lib/fetch";
import { DateRangePickerTrigger } from "@/src/components/ui/date-range-picker";
import { format, parseISO } from "date-fns";
import { ComboboxMulti } from "@/src/components/ui/combobox-multi";
import { SearchParams } from "./search_params.model";

export type SearchBarProps = {
  params: SearchParams;
  setSearchParams: (p: SearchParams) => void;
};

export function SearchBar(props: SearchBarProps) {
  const [place, setPlace] = useState<{ id: string; type: string; name: string } | null>(null);
  const { fetch } = useFetch();

  const datesStr = props.params.period
    ? `${format(parseISO(props.params.period.dateFrom), "dd MMM")} - ${format(parseISO(props.params.period.dateTo), "dd MMM")}`
    : "Add dates";

  return (
    <div className="bg-white border border-blue-100 rounded-lg px-6 py-4 mb-8">
      <div className="flex justify-between items-center gap-x-8">
        <div className="flex flex-1 flex-col basis-1/4 relative after:content-[''] after:w-[1px] after:h-full after:bg-slate-300 after:flex after:absolute after:top-0 after:-right-4">
          <div className="relative">
            <div className="flex flex-col items-start">
              <Label className="text-base font-normal">Where</Label>
              <span
                className={`text-base inline-block text-left break-all ${place ? "text-black" : "text-gray-400"} !m-0 w-full`}
              >
                <Combobox
                  className="w-full h-6 border-none p-0"
                  placeholder="Add destination"
                  loadOptions={loadPlaces}
                  renderOption={(o) => `${o.name} (${o.type})`}
                  value={place}
                  onChange={(o) => {
                    props.setSearchParams({
                      ...props.params,
                    });
                  }}
                />
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col basis-1/4 relative after:content-[''] after:w-[1px] after:h-full after:bg-slate-300 after:flex after:absolute after:top-0 after:-right-4">
          <div className="relative">
            <Label className="text-base font-normal">When</Label>
            <DateRangePickerTrigger
              onChange={(v) =>
                props.setSearchParams({
                  ...props.params,
                  period: {
                    dateFrom: v!.startDate,
                    dateTo: v!.endDate,
                  } as any,
                })
              }
            >
              <button
                className={`text-base text-left break-all ${datesStr ? "text-black" : "text-gray-400"} !m-0 line-clamp-1 max-w-[200px]`}
              >
                {datesStr}
              </button>
            </DateRangePickerTrigger>
          </div>
        </div>
        <div className="flex flex-1 flex-col basis-1/4 relative">
          <div className="relative">
            <div className="flex flex-col items-start">
              <Label className="text-base font-normal">Boat type</Label>
              <ComboboxMulti
                className="h-6 border-none p-0"
                placeholder="Select boat types"
                onChange={(boatTypes) => {
                  props.setSearchParams({
                    ...props.params,
                    boatType: boatTypes as any,
                  });
                }}
                loadOptions={() => boatTypes}
                value={props.params.boatType ?? []}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

async function loadPlaces(filter: string): Promise<{ id: string; type: string; name: string }[]> {
  const res = [] as { id: string; type: string; name: string }[];
  let rem = 10;

  const [l, r, c] = await Promise.all([
    fetch<{ data: { id: string; names: string }[] }>(`/api/locations/locations?filter=${filter}&pageSize=5`),
    fetch<{ data: { id: string; names: string }[] }>(`/api/locations/regions?filter=${filter}&pageSize=5`),
    fetch<{ data: { id: string; names: string }[] }>(`/api/locations/countries?filter=${filter}&pageSize=5`),
  ]);

  res.push(...c.data.map((v) => ({ id: v.id, type: "country", name: v.names.split("\n")[0] })).slice(0, rem));
  rem = 10 - res.length;
  res.push(...r.data.map((v) => ({ id: v.id, type: "region", name: v.names.split("\n")[0] })).slice(0, rem));
  rem = 10 - res.length;
  res.push(...l.data.map((v) => ({ id: v.id, type: "location", name: v.names.split("\n")[0] })).slice(0, rem));

  return res;
}

const boatTypes = [
  {
    value: "Sailing yacht",
    label: "Sailing yacht",
  },
  {
    value: "Catamaran",
    label: "Catamaran",
  },
  {
    value: "Do not display",
    label: "Do not display",
  },
  {
    value: "Gulet",
    label: "Gulet",
  },
  {
    value: "Jet ski",
    label: "Jet ski",
  },
  {
    value: "Power catamaran",
    label: "Power catamaran",
  },
  {
    value: "R.I.B / Speed boat",
    label: "R.I.B / Speed boat",
  },
  {
    value: "Other",
    label: "Other",
  },
  {
    value: "Motor yacht",
    label: "Motor yacht",
  },
];
